import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './upsetPlot.Renderer';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { convertColumnDataToDisplayString } from '../../helpers/misc';

export function UpsetPlot({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const {
    definition,
    areaBars,
    dataTable,
    dataValue,
    dataItemId,
    name,
    setsColumnInfo,
    hasSummedValue,
    summedValueColumnInfo,
    itemCount,
    sets,
    intersections,
    intersectionsMap,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config);

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'bars' && mappingInfo.area?.id != 'sets') return null;
    const intersectionIdx = Math.floor(logicPositX * intersections.length);
    if (intersectionIdx >= 0 && intersectionIdx < intersections.length) {
      return {
        dataTableId: dataBinding.dataTableId,
        catColumnId: setsColumnInfo.id,
        itemId: intersections[intersectionIdx].intersectionId,
        custom: {
          intersectionIdx,
        },
      };
    }
    return null;
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId != setsColumnInfo.id) return null;
    const labels: string[] = [];
    if (context.isActiveChart && intersectionsMap[hoverInfo.itemId]) {
      const intersection = intersectionsMap[hoverInfo.itemId];
      labels.push(intersection.label);
      labels.push(`Count: ${intersection.selectedCount} / ${intersection.count}`);
      if (hasSummedValue && summedValueColumnInfo) {
        const valFormatted = convertColumnDataToDisplayString(summedValueColumnInfo, intersection.summedValue);
        const valSelectedFormatted = convertColumnDataToDisplayString(
          summedValueColumnInfo,
          intersection.summedValueSelected,
        );
        labels.push(`Total: ${valSelectedFormatted} / ${valFormatted}`);
      }
      return {
        area: areaBars,
        logicPosX: (hoverInfo.custom!.intersectionIdx + 0.5) / intersections.length,
        logicPosY: 0,
        labels,
      };
    }
    return null;
  }

  function onItemClicked(selectedIntersectionId: string) {
    const intersection = intersectionsMap[selectedIntersectionId];
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.INTERSECTIONS,
      dataTableId: dataTable.id,
      colId: setsColumnInfo.id,
      setIds: intersection.membershipIds,
    });
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        widgetContext={widgetContext}
        onItemClicked={onItemClicked}
        maxZoomXAbsolute={intersections.length * 20}
        maxZoomYAbsolute={sets.length * 20}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
